import React, { useEffect, useRef } from 'react';
import { Plus, Check, RotateCcw } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  getScientificPalettes,
  getSingleColorPalettes,
  getPalettePreview,
  getDefaultPalette
} from '../../../utils/colorPalettes';

const ColorPicker = ({ layer, onClose, onUpdateTheme }) => {
  const pickerRef = useRef(null);
  // Always use palette mode
  const currentPalette = layer.config?.palette || getDefaultPalette();
  const isPaletteSelected = true; // Always using palette mode now
  const isInverted = layer.config?.inverted || false;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.closest('[data-active-trigger="true"]')) return;
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handlePaletteSelect = (paletteName) => {
    // Update with palette name, preserve invert state
    onUpdateTheme(layer.id, null, paletteName, isInverted);
  };

  const handleInvertToggle = () => {
    // Toggle invert state
    // If no palette is selected, default to viridis when inverting
    const paletteToUse = currentPalette || getDefaultPalette();
    onUpdateTheme(layer.id, null, paletteToUse, !isInverted);
  };

  const scientificPalettes = getScientificPalettes();
  const singleColorPalettes = getSingleColorPalettes();

  return (
    <div
      ref={pickerRef}
      className="absolute left-[calc(100%+12px)] top-0 z-[100] w-64 bg-white dark:bg-gray-900 p-4 rounded-[24px] shadow-2xl border border-gray-100 dark:border-gray-800 animate-in fade-in slide-in-from-left-2 duration-200 max-h-[80vh] overflow-y-auto"
    >
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1.5">
          <h6 className="text-[10px] font-black text-gray-900 dark:text-white uppercase tracking-widest">Select Palette</h6>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
            <Plus size={14} className="rotate-45" />
          </button>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-[8px] text-gray-500 dark:text-gray-400 font-medium">
            {isInverted ? 'Palettes shown inverted (reversed)' : 'Click invert to reverse color order'}
          </p>
          <button
            onClick={handleInvertToggle}
            className={cn(
              "px-2.5 py-1.5 rounded-lg transition-all flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-wider",
              isInverted
                ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400"
                : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
            )}
            title="Invert all palettes (reverse color order from low to high)"
          >
            <RotateCcw size={12} />
            <span>Invert</span>
          </button>
        </div>
      </div>

      {/* Scientific Palettes */}
      <div className="mb-4">
        <div className="text-[9px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-2">Scientific</div>
        <div className="grid grid-cols-2 gap-2">
          {scientificPalettes.map(paletteName => {
            const isSelected = isPaletteSelected && currentPalette === paletteName;
            return (
              <button
                key={paletteName}
                onClick={() => handlePaletteSelect(paletteName)}
                className={cn(
                  "relative rounded-lg transition-all overflow-hidden group",
                  isSelected
                    ? "ring-2 ring-emerald-500 ring-offset-2 dark:ring-offset-gray-900 scale-105"
                    : "hover:scale-105 ring-1 ring-gray-200 dark:ring-gray-700"
                )}
                style={{
                  background: getPalettePreview(paletteName, isInverted),
                  height: '40px'
                }}
                title={paletteName.charAt(0).toUpperCase() + paletteName.slice(1)}
              >
                {isSelected && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <Check size={16} className="text-white drop-shadow-lg" strokeWidth={3} />
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/40 to-transparent p-1.5">
                  <span className="text-[8px] font-bold text-white uppercase tracking-wider drop-shadow">
                    {paletteName}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Sequential Palettes (colorblind-friendly alternatives) */}
      <div>
        <div className="text-[9px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-2">Sequential</div>
        <div className="grid grid-cols-2 gap-2">
          {singleColorPalettes.map(paletteName => {
            const isSelected = isPaletteSelected && currentPalette === paletteName;
            return (
              <button
                key={paletteName}
                onClick={() => handlePaletteSelect(paletteName)}
                className={cn(
                  "relative rounded-lg transition-all overflow-hidden group",
                  isSelected
                    ? "ring-2 ring-emerald-500 ring-offset-2 dark:ring-offset-gray-900 scale-105"
                    : "hover:scale-105 ring-1 ring-gray-200 dark:ring-gray-700"
                )}
                style={{
                  background: getPalettePreview(paletteName, isInverted),
                  height: '40px'
                }}
                title={paletteName.charAt(0).toUpperCase() + paletteName.slice(1)}
              >
                {isSelected && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <Check size={16} className="text-white drop-shadow-lg" strokeWidth={3} />
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/40 to-transparent p-1.5">
                  <span className="text-[8px] font-bold text-white uppercase tracking-wider drop-shadow">
                    {paletteName}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ColorPicker;
